import { useEffect, useReducer } from 'react';
import { isEqual } from 'lodash';
import { useClient } from '../../../../context/client';
import { useOrderStatuses } from '../../../../context/OrderStatuses';
import { useUser } from '../../../../context/user';
import { useAsyncEffect } from '../../../../hooks';

const ORDER_ACTIONS = {
	UPDATE_LOADING: 'updateLoading',
	LOAD_USER: 'loadUser',
	LOAD_ORDER: 'loadOrder',
	ADD_TAG: 'addTag',
	REMOVE_TAG: 'removeTag',
	ADD_PHOTOS: 'addPhotos',
	REMOVE_PHOTO: 'removePhoto',
	UPDATE_STATUS: 'updateStatus',
	ADD_NOTE: 'addNote',
	UPDATE_VENDOR: 'updateVendor',
	UPDATE_LINE_ITEM: 'updateLineItem',
	UPDATE_SHIPPING_ADDRESS: 'updateShippingAddress',
	UPDATE_CUSTOMER_BILLING: 'updateCustomerBilling',
	UPDATE_CUSTOMER_SHIPPING: 'updateCustomerShipping',
	UPDATE_SHIP_WITH: 'updateShipWith',
	UPDATE_INTERNAL_TRACKING_NUMBER: 'updateInternalTrackingNumber',
	UPDATE_ORDER_SUCCESS: 'updateOrderSuccess',
};

const TAG_TYPES = {
	TAGS: 'tags',
	CHILD_TAGS: 'childTags',
};

const TAG_LOG_NAMES = {
	[TAG_TYPES.TAGS]: 'Tag',
	[TAG_TYPES.CHILD_TAGS]: 'Sub Tag',
};

function addLogReducer(state, message) {
	let userText = '';
	const messages = message instanceof Array ? message : [message];

	if (state.user) userText = `(${state.user}) - `;

	return {
		...state,
		custom: {
			...state.custom,
			logs: [
				...state.custom.logs,
				...messages.map((text) => {
					const d = new Date();
					const base = d.toString();
					const ms = String(d.getMilliseconds());
					const withMs = base.replace(/(\d{2}:\d{2}:\d{2})/, `$1.${ms}`);
					const ts = `[${withMs.split(' (')[0]}]`;
					return {
						message: `${ts} ${userText}${text}`,
						status: 'Not saved',
					};
				}),
			],
		},
	};
}

function loadOrderReducer(state, action) {
	const {
		order = {},
		custom: { tags = [], sub_tags = [], ...custom } = {},
		previousOrderId,
		nextOrderId,
	} = action.data;

	return {
		...state,
		order,
		tags,
		childTags: sub_tags,
		custom,
		previousOrderId,
		nextOrderId,
	};
}

function isValidTagType(type) {
	return Object.values(TAG_TYPES).includes(type);
}

function hasActionTag(state, action) {
	return state[action.data.type].includes(action.data.tag);
}

function addTagsReducer(state, action) {
	// If the tag type is not valid or the tag already exists, don't update.
	if (!isValidTagType(action.data.type) || hasActionTag(state, action))
		return state;

	return {
		...addLogReducer(
			state,
			`${TAG_LOG_NAMES[action.data.type]} "${action.data.tag}" added.`,
		),
		[action.data.type]: [...state[action.data.type], action.data.tag],
	};
}

/**
 * Returns a new order state with a tag and log entry added.
 */
function buildStateWithAddedTag(state, type, tag) {
	return addTagsReducer(state, { data: { type, tag } });
}

function removeTagsReducer(state, action) {
	// If the tag type is not valid or the tag does not already exist, don't update.
	if (!isValidTagType(action.data.type) || !hasActionTag(state, action))
		return state;

	return {
		...addLogReducer(
			state,
			`${TAG_LOG_NAMES[action.data.type]} "${action.data.tag}" removed.`,
		),
		[action.data.type]: state[action.data.type].filter(
			(tag) => tag !== action.data.tag,
		),
	};
}

function addPhotoReducer(state, action) {
	return addLogReducer(
		{
			...state,
			custom: {
				...state.custom,
				photos: [
					...state.custom.photos,
					...action.data.filter(
						(photo) => !state.custom.photos.includes(photo),
					),
				],
			},
		},
		`Photo added.`,
	);
}

function removePhotoReducer(state, action) {
	return addLogReducer(
		{
			...state,
			custom: {
				...state.custom,
				photos: state.custom.photos.filter(
					(photo) => photo !== action.data,
				),
			},
		},
		`Photo removed.`,
	);
}

function changeStatusReducer(state, action) {
	let newStatus;
	state.orderStatuses.forEach((status) => {
		if (status.id === action.data) newStatus = status.name;
	});
	return addLogReducer(
		{
			...state,
			order: {
				...state.order,
				status_id: action.data,
			},
		},
		`Order status changed to ${newStatus}.`,
	);
}

function updateVendorReducer(state, action) {
	const { products, vendor: newVendor } = action.data;
	const logMessages = [];

	const updatedState = {
		...state,
		custom: {
			...state.custom,
			products: state.custom.products.map((product) => {
				// Skip products not specified
				if (!products.includes(product.id)) return product;

				logMessages.push(
					`Vendor changed from "${product.vendor}" to ${newVendor}.`,
				);

				return {
					...product,
					vendor: newVendor,
				};
			}),
		},
	};

	return addLogReducer(updatedState, logMessages);
}

function updateShippingAddressReducer(state, action) {
	const { products, shippingAddress: newShippingAddress } = action.data;
	const logMessages = [];

	const updatedState = {
		...state,
		custom: {
			...state.custom,
			products: state.custom.products.map((product) => {
				// Skip products not specified
				if (!products.includes(product.id)) return product;

				logMessages.push(
					`Shipping Address changed from "${product.shipping_address}" to ${newShippingAddress}.`,
				);

				return {
					...product,
					shipping_address: newShippingAddress,
				};
			}),
		},
	};

	return addLogReducer(updatedState, logMessages);
}

function updateCustomerBillingReducer(state, action) {
	if (!isEqual(state.order.billing_address, action.data)) {
		return addLogReducer(
			{
				...state,
				order: {
					...state.order,
					billing_address: action.data,
				},
			},

			`Customer billing address information changed from ${state.order.billing_address.first_name} ${state.order.billing_address.last_name}, ${state.order.billing_address.street_1} ${state.order.billing_address.street_2} ${state.order.billing_address.city}, ${state.order.billing_address.state}, ${state.order.billing_address.country} ${state.order.billing_address.zip}, ${state.order.billing_address.phone} ${state.order.billing_address.email} to ${action.data.first_name}${action.data.last_name}, ${action.data.street_1} ${action.data.street_2} ${action.data.city}, ${action.data.state}, ${action.data.country} ${action.data.zip}, ${action.data.phone} ${action.data.email}.`,
		);
	}
	return state;
}

function updateCustomerShippingReducer(state, action) {
	if (!isEqual(state.order.shippingAddresses[0], action.data)) {
		return addLogReducer(
			{
				...state,
				order: {
					...state.order,
					shippingAddresses: [action.data],
				},
			},
			`Shipping address changed from ${state.order.shippingAddresses[0].first_name} ${state.order.shippingAddresses[0].last_name}, ${state.order.shippingAddresses[0].street_1} ${state.order.shippingAddresses[0].street_2} ${state.order.shippingAddresses[0].city}, ${state.order.shippingAddresses[0].state}, ${state.order.shippingAddresses[0].country} ${state.order.shippingAddresses[0].zip} to ${action.data.first_name} ${action.data.last_name}, ${action.data.street_1} ${action.data.street_2} ${action.data.city}, ${action.data.state}, ${action.data.country} ${action.data.zip}.`,
		);
	}
	return state;
}

function updateLineItemReducer(state, action) {
	const oldLineItem = state.custom.products.find(
		({ id }) => action.data.id === id,
	);

	// Gather all the changes and add them as log messages
	const logMessages = [
		...Object.entries(oldLineItem)
			.filter(([property, value]) => value !== action.data[property])
			.map(
				([property, value]) =>
					`Updated ${property} from ${value} to ${action.data[property]}`,
			),
	];

	const productIndex = state.custom.products.findIndex(
		({ id }) => action.data.id === id,
	);

	return addLogReducer(
		{
			...state,
			custom: {
				...state.custom,
				products: [
					...state.custom.products.slice(0, productIndex),
					action.data,
					...state.custom.products.slice(productIndex + 1),
				],
			},
		},
		logMessages,
	);
}

function updateShipWithReducer(state, action) {
	state.custom.logs = cleanRepetitiveLogEntries(
		state.custom.logs,
		'Ship With Order',
	);
	return addLogReducer(
		{
			...state,
			custom: {
				...state.custom,
				ship_with: action.data,
			},
		},
		`Updated Ship With Order information: ${action.data}`,
	);
}

function updateInternalTrackingNumberReducer(state, action) {
	state.custom.logs = cleanRepetitiveLogEntries(
		state.custom.logs,
		'Internal Tracking Number',
	);
	return addLogReducer(
		{
			...state,
			custom: {
				...state.custom,
				internal_tracking_number: action.data,
			},
		},
		`Updated Internal Tracking Number: ${action.data}`,
	);
}

// Some events trigger with an onChange event.
// This generates multiple log entries for each keypress.
// This functiona loops the custom logs and remove any unwanted log entires
// that may seem repeated.
function cleanRepetitiveLogEntries(logs, msg) {
	for (let index = 0; index < logs.length; index++) {
		const element = logs[index];
		if (element.status === 'Not saved' && element.message.includes(msg))
			logs.splice(index, 1);
	}

	return logs;
}

function updateOrderSuccessReducer(state, action) {
	const { date_modified, status_id, status } = action.data;
	return {
		...state,
		order: {
			...state.order,
			date_modified,
			status_id,
			status,
		},
	};
}

function orderDataReducer(state, action) {
	switch (action.type) {
		case ORDER_ACTIONS.UPDATE_LOADING:
			return { ...state, isLoading: !!action.data };
		case ORDER_ACTIONS.LOAD_USER:
			return { ...state, user: action.data };
		case ORDER_ACTIONS.LOAD_ORDER:
			return loadOrderReducer(state, action);
		case ORDER_ACTIONS.ADD_TAG:
			return addTagsReducer(state, action);
		case ORDER_ACTIONS.REMOVE_TAG:
			return removeTagsReducer(state, action);
		case ORDER_ACTIONS.ADD_PHOTOS:
			return addPhotoReducer(state, action);
		case ORDER_ACTIONS.REMOVE_PHOTO:
			return removePhotoReducer(state, action);
		case ORDER_ACTIONS.ADD_NOTE:
			return addLogReducer(state, action.data);
		case ORDER_ACTIONS.UPDATE_STATUS:
			return changeStatusReducer(state, action);
		case ORDER_ACTIONS.UPDATE_VENDOR:
			return updateVendorReducer(state, action);
		case ORDER_ACTIONS.UPDATE_SHIPPING_ADDRESS:
			return updateShippingAddressReducer(state, action);
		case ORDER_ACTIONS.UPDATE_LINE_ITEM:
			return updateLineItemReducer(state, action);
		case ORDER_ACTIONS.UPDATE_CUSTOMER_SHIPPING:
			return updateCustomerShippingReducer(state, action);
		case ORDER_ACTIONS.UPDATE_CUSTOMER_BILLING:
			return updateCustomerBillingReducer(state, action);
		case ORDER_ACTIONS.UPDATE_SHIP_WITH:
			return updateShipWithReducer(state, action);
		case ORDER_ACTIONS.UPDATE_INTERNAL_TRACKING_NUMBER:
			return updateInternalTrackingNumberReducer(state, action);
		case ORDER_ACTIONS.UPDATE_ORDER_SUCCESS:
			return updateOrderSuccessReducer(state, action);
		default:
			return state;
	}
}

function useOrderEditState(id) {
	const client = useClient();
	const user = useUser();
	const orderStatuses = useOrderStatuses();
	const [state, dispatch] = useReducer(orderDataReducer, {
		user,
		isLoading: false,
		order: {},
		custom: {},
		tags: [],
		childTags: [],
		previousOrderId: null,
		nextOrderId: null,
		orderStatuses: orderStatuses,
	});

	useEffect(
		() => dispatch({ type: ORDER_ACTIONS.LOAD_USER, data: user }),
		[user],
	);
	useAsyncEffect(async () => {
		try {
			dispatch({ type: ORDER_ACTIONS.UPDATE_LOADING, data: true });

			const {
				meta: { previousOrderId, nextOrderId },
				...order
			} = (await client.getOrderById(id)) ?? {};

			dispatch({
				type: ORDER_ACTIONS.LOAD_ORDER,
				data: { ...order, previousOrderId, nextOrderId },
			});
		} catch (err) {
			dispatch({
				type: ORDER_ACTIONS.LOAD_ORDER,
				data: {
					order: {},
					custom: {},
					tags: [],
					childTags: [],
				},
			});
			console.error(err);
		}

		dispatch({ type: ORDER_ACTIONS.UPDATE_LOADING, data: false });
	}, [id]);

	return [state, dispatch];
}

export {
	useOrderEditState,
	ORDER_ACTIONS,
	TAG_TYPES,
	buildStateWithAddedTag,
};
